import { createFileRoute, notFound } from "@tanstack/react-router";
import AuthorProfile, { authorProfileQueryOptions } from "@/pages/AuthorProfile";
import { decodeEntities } from "@/lib/articleQuery";
import { getSearchDescription } from "@/lib/searchDescriptions";

const SITE = "https://www.garden-review.co.uk";

const clip = (text: string, max = 160) => {
  const t = decodeEntities(text.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const i = cut.lastIndexOf(" ");
  return `${i > max * 0.6 ? cut.slice(0, i) : cut}.`;
};

export const Route = createFileRoute("/authors/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(authorProfileQueryOptions(params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/authors/${params.slug}`;
    const author = loaderData?.author;
    if (!author) return { links: [{ rel: "canonical", href: url }] };
    const role = author.job_title || author.role;
    const title = `${author.name}${role ? ` – ${role}` : ""} | Garden-Review`;
    const description = getSearchDescription(`/authors/${params.slug}`, clip(
      author.bio || author.meta_description || `Garden articles and guides written by ${author.name} on Garden-Review.`,
    ));
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { name: "author", content: author.name },
      { property: "og:type", content: "profile" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { name: "twitter:card", content: author.avatar_url ? "summary" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    if (author.avatar_url?.startsWith("https://")) {
      meta.push(
        { property: "og:image", content: author.avatar_url },
        { name: "twitter:image", content: author.avatar_url },
      );
    }
    return { meta, links: [{ rel: "canonical", href: url }] };
  },
  component: AuthorProfile,
});
